import { defineStore } from 'pinia';
import api from '@/api';

export interface Result {
  id: number;
  event_id: number;
  admin_id: number | null;
  provider_id: number;
  result: string;
  draw_date: string;
  created_at: string;
  updated_at: string;
  provider: string;
  game_name: string;
  game_slug: string;
  game_logo: string;
  cancelled: boolean;
}

export interface ResultsFilters {
  drawDate?: string;
  drawType?: string;
  drawId?: string;
  gameSlug?: string;
}

let resultsRequestSeq = 0;

export const useResultsStore = defineStore('results', {
  state: () => ({
    results: [] as Result[],
    games: [] as any[],
    loading: false,
    error: null as string | null,
    filters: {} as ResultsFilters,
    pagination: {
      total_count: 0,
      total_pages: 1,
      current_page: 1,
      limit: 20,
    },
  }),
  actions: {
    setFilters(filters: ResultsFilters) {
      this.filters = { ...this.filters, ...filters };
    },
    async fetchResults(page = 1, options?: { append?: boolean }) {
      const append = options?.append === true;
      const requestId = ++resultsRequestSeq;
      this.loading = true;
      this.error = null;
      try {
        const payload = {
          ...this.filters,
          page,
          limit: this.pagination.limit,
        };
        const { data } = await api.post('/results', payload);
        if (requestId !== resultsRequestSeq) return;

        // Defensive: handle both v1 and v2 API response shapes
        let results: Result[] = [];
        let games = [];
        let paginator = { ...this.pagination, current_page: page };

        if (data && data.result) {
          if (Array.isArray(data.result.results)) {
            results = data.result.results;
          } else if (data.result.result && Array.isArray(data.result.result.results)) {
            results = data.result.result.results;
          }
          if (Array.isArray(data.result.games)) {
            games = data.result.games;
          } else if (data.result.result && Array.isArray(data.result.result.games)) {
            games = data.result.result.games;
          }
        }
        if (data.paginator) {
          paginator = { ...paginator, ...data.paginator };
        }

        if (append) {
          const seen = new Set(this.results.map((result) => result.id));
          this.results = [
            ...this.results,
            ...results.filter((result) => !seen.has(result.id)),
          ];
        } else {
          this.results = results;
        }
        this.games = games;
        this.pagination = paginator;
      } catch (e: any) {
        if (requestId !== resultsRequestSeq) return;
        this.error = e?.userMessage || e?.message || 'Failed to fetch results';
      } finally {
        if (requestId === resultsRequestSeq) {
          this.loading = false;
        }
      }
    },
    async loadMore() {
      const nextPage = Number(this.pagination.current_page || 1) + 1;
      const totalPages = Number(this.pagination.total_pages || 1);
      if (this.loading || nextPage > totalPages) return;
      await this.fetchResults(nextPage, { append: true });
    },
    setPage(page: number) {
      this.fetchResults(page);
    },
  },
}); 